import { useState, type ReactNode } from "react";

import { useLive } from "../lib/use-live";
import {money} from "../lib/format";
import { useT } from "../context/locale";
import { useAuth } from "../context/auth";
import type { TranslationKey } from "../i18n";
import { useRelative, useDateTime } from "../lib/use-relative";
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Panel,
  Table,
  Td,
  Tr,
} from "../components/ui";
import {
  FilterBar,
  PageBar,
  SortHeader,
  useFilters,
  useSort,
} from "../components/ui/filters";
import type { AdminProfile, AuditEntry, AuditScope, Paged } from "../types/api";

const ACTIONS = [
  "user.approve",
  "user.reject",
  "user.create",
  "user.update",
  "user.ban",
  "user.delete",
  "user.balance_set",
  "user.history_generate",
  "bet.update",
  "bet.delete",
  "draw.manual_result",
  "draw.result_edit",
  "draw.void",
  "draw.delete",
  "transaction.update",
  "transaction.delete",
  "admin.create",
  "admin.update",
  "admin.delete",
  "admin.password_change",
  "settings.update",
  "referral_code.create",
];

/** Actions that change money or results deserve to stand out in a long list. */
const SENSITIVE = new Set([
  "user.balance_set",
  "user.delete",
  "user.ban",
  "draw.manual_result",
  "draw.result_edit",
  "draw.delete",
  "bet.delete",
  "bet.update",
  "transaction.delete",
  "admin.create",
  "admin.update",
  "admin.delete",
]);

export function AuditLog() {
  const dateTime = useDateTime();
  const t = useT();
  const relative = useRelative();

  /** Audit actions are a closed set, so each gets its own key. */
  const { can } = useAuth();
  const actionLabel = (action: string) => t(`audit.${action}` as TranslationKey);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(30);

  const filters = useFilters(
    { action: "", operator: "", from: "", to: "", targetId: "" },
    () => setPage(1),
  );

  /*
   * The operator list, for the filter's dropdown.
   *
   * Only fetched for a superadmin — they are the only one the API honours an
   * `adminId` from, and listing the staff is their screen anyway. `enabled`
   * rather than a conditional hook, so the subscription simply is not opened.
   */
  const operators = useLive<Paged<AdminProfile>>(
    "admins",
    { perPage: 100, sort: "username", order: "asc" },
    { enabled: can("superadmin") },
  );
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const log = useLive<Paged<AuditEntry> & { scope?: AuditScope }>("audit", {
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
        action: filters.applied.action || undefined,
        // Ignored by the API for anyone below superadmin, which is why the
        // control below is only offered to them.
        adminId: filters.applied.operator || undefined,
        targetId: filters.applied.targetId || undefined,
        from: filters.applied.from
          ? new Date(filters.applied.from).toISOString()
          : undefined,
        to: filters.applied.to
          ? new Date(`${filters.applied.to}T23:59:59`).toISOString()
          : undefined,
      });

  /**
   * Whether this trail holds only the signed-in operator's own actions.
   *
   * Read from the response rather than worked out from the local role: the API
   * is what decides the scope, and a console that made its own guess would be
   * one deploy away from saying something the server disagrees with.
   */
  const ownOnly = log.data?.scope === "own";

  return (
    <div className="space-y-5">
      <FilterBar
        loading={log.loading}
        state={filters}
        filters={[
          {
            key: "action",
            label: t("col.action"),
            type: "select",
            options: [
              { value: "", label: t("audit.filter.anyAction") },
              ...ACTIONS.map((value) => ({ value, label: actionLabel(value) })),
            ],
          },
          // Offered to a superadmin only: they are the only role whose trail
          // is not already scoped to themselves, so for anybody else this
          // control could not change what comes back.
          ...(can("superadmin")
            ? [
                {
                  key: "operator",
                  label: t("col.operator"),
                  type: "select" as const,
                  options: [
                    { value: "", label: t("audit.filter.anyOperator") },
                    ...(operators.data?.items ?? []).map((item) => ({
                      value: item.id,
                      label: `${item.displayName || item.username} · ${t(
                        `admins.role.${item.role}` as TranslationKey,
                      )}`,
                    })),
                  ],
                },
              ]
            : []),
          {
            key: "targetId",
            label: t("audit.filter.targetId"),
            placeholder: t("audit.filter.recordId"),
          },
          { key: "from", label: t("common.from"), type: "date" },
          { key: "to", label: t("common.to"), type: "date" },
        ]}
      />

      <div className="space-y-5">
        <div
          className="panel px-5 py-3"
          style={{ borderLeft: "3px solid var(--accent)" }}
        >
          <p className="text-muted text-[12px]">{t("audit.note")}</p>
          {/*
            Said out loud rather than left to be inferred. An operator who can
            only see their own actions is looking at a short list, and a short
            list reads as a quiet day unless something tells them otherwise.
            The API decides the scope; this only reports it.
          */}
          {ownOnly ? (
            <p className="text-dim mt-1 text-[12px]">{t("audit.scopedToYou")}</p>
          ) : null}
        </div>

        <Panel
          eyebrow={t("section.record")}
          title={t("audit.title")}
          bodyClassName=""
          actions={
            <Button size="sm" onClick={log.reload}>
              {t("action.refresh")}
            </Button>
          }
        >
          {log.error ? (
            <div className="p-5">
              <ErrorNote message={log.error} onRetry={log.reload} />
            </div>
          ) : (
            <>
              <Table
                head={[
                  <SortHeader key="c" field="createdAt" state={sort}>
                    {t("common.when")}
                  </SortHeader>,
                  <SortHeader key="o" field="operator" state={sort}>
                    {t("col.operator")}
                  </SortHeader>,
                  <SortHeader key="a" field="action" state={sort}>
                    {t("col.action")}
                  </SortHeader>,
                  t("col.target"),
                  t("col.change"),
                  t("common.reason"),
                ]}
              >
                {log.loading ? (
                  <LoadingRows columns={6} />
                ) : log.data && log.data.items.length === 0 ? (
                  <Tr>
                    <Td colSpan={6}>
                      <EmptyState
                        title={t("audit.empty")}
                        detail={t("audit.emptyDetail")}
                      />
                    </Td>
                  </Tr>
                ) : (
                  (log.data?.items ?? []).map((entry) => (
                    <Tr key={entry.id}>
                      <Td className="text-[12px] whitespace-nowrap">
                        <div>{dateTime(entry.createdAt)}</div>
                        <div className="text-dim text-[11px]">
                          {relative(entry.createdAt)}
                        </div>
                      </Td>
                      <Td className="text-[12px] font-semibold">
                        {entry.admin.username}
                      </Td>
                      <Td>
                        <Badge
                          tone={
                            SENSITIVE.has(entry.action) ? "warning" : "neutral"
                          }
                        >
                          {actionLabel(entry.action)}
                        </Badge>
                      </Td>
                      <Td className="text-[12px]">
                        {entry.target ? (
                          <>
                            {entry.target.label?.title ? (
                              <div className="font-semibold">
                                {entry.target.label.title}
                              </div>
                            ) : null}
                            {entry.target.label?.detail ? (
                              <div className="money text-[11px]">
                                {entry.target.label.detail}
                              </div>
                            ) : null}
                            <div
                              className="money text-dim text-[11px]"
                              title={entry.target.id}
                            >
                              {entry.target.type}:{entry.target.id.slice(-6)}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td className="text-[12px]">
                        <ChangeSummary entry={entry} />
                      </Td>
                      <Td className="text-dim max-w-[18rem] text-[12px] break-words">
                        {entry.reason ?? "—"}
                      </Td>
                    </Tr>
                  ))
                )}
              </Table>

              {log.data ? (
                <PageBar
                  page={log.data.page}
                  pages={log.data.pages}
                  total={log.data.total}
                  perPage={perPage}
                  onPage={setPage}
                  onPerPage={(size) => {
                    setPerPage(size);
                    setPage(1);
                  }}
                />
              ) : null}
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

/** Bookkeeping the actions record alongside the change, not part of it. */
const HIDDEN_FIELDS = new Set(["id", "updatedAt"]);

/** "stakeCents" → "stake", "bankAccountNo" → "bank account no". */
function fieldLabel(key: string) {
  return key
    .replace(/Cents$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Every field the action changed, as "field: before → after".
 *
 * An action with no `before` (a create, a run) lists what it wrote instead.
 * Balance moves keep their dedicated line with the signed delta, since that is
 * the figure an operator is scanning for.
 */
function ChangeSummary({ entry }: { entry: AuditEntry }) {
  const t = useT();
  const dateTime = useDateTime();
  const before = (entry.before ?? {}) as Record<string, unknown>;
  const after = (entry.after ?? {}) as Record<string, unknown>;

  const format = (key: string, value: unknown): string => {
    if (value === null || value === undefined || value === "") return "—";
    if (key.endsWith("Cents") && typeof value === "number") return money(value);
    if (key === "status" && typeof value === "string")
      return t(`status.${value}` as TranslationKey);
    if (typeof value === "string" && ISO_DATE.test(value)) return dateTime(value);
    if (Array.isArray(value))
      return value.every((item) => typeof item !== "object")
        ? value.join(" · ")
        : `${value.length}×`;
    if (typeof value === "object") return JSON.stringify(value).slice(0, 80);
    return String(value);
  };

  const rows: ReactNode[] = [];

  if (
    typeof before.balanceCents === "number" &&
    typeof after.balanceCents === "number"
  ) {
    const delta = after.balanceCents - before.balanceCents;
    rows.push(
      <div key="balance" className="money">
        {money(before.balanceCents)} →{" "}
        <span style={{ color: "var(--accent)" }}>
          {money(after.balanceCents)}
        </span>{" "}
        <span
          style={{ color: delta >= 0 ? "var(--success)" : "var(--danger)" }}
        >
          ({delta >= 0 ? "+" : "−"}
          {money(Math.abs(delta)).replace("MYR ", "")})
        </span>
      </div>,
    );
  }

  const hasBefore = entry.before && Object.keys(entry.before).length > 0;
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => !HIDDEN_FIELDS.has(key) && key !== "balanceCents",
  );

  for (const key of keys) {
    const was = format(key, before[key]);
    const now = format(key, after[key]);
    if (hasBefore && key in before && key in after) {
      if (was === now) continue;
      rows.push(
        <div key={key}>
          <span className="text-dim">{fieldLabel(key)}:</span>{" "}
          <span className="money">{was}</span> →{" "}
          <span className="money" style={{ color: "var(--accent)" }}>
            {now}
          </span>
        </div>,
      );
    } else {
      // Recorded on one side only: a snapshot of what was removed, or of
      // what was written.
      rows.push(
        <div key={key}>
          <span className="text-dim">{fieldLabel(key)}:</span>{" "}
          <span className="money">{key in after ? now : was}</span>
        </div>,
      );
    }
  }

  if (!rows.length)
    return <span className="text-dim">{hasBefore ? t("audit.noChange") : "—"}</span>;
  return <div className="space-y-0.5 text-[12px]">{rows}</div>;
}
